import { Button, Dropdown, Input, message, Select, Tabs } from "antd";
import type { ProjectVo } from "@zhihaoscm/entity";
import {
    PROJECT_STATE,
    PROJECT_TYPE,
    PURCHASE_PAYMENT_TYPE,
    PURCHASE_TYPE,
    ROLE_CODE,
    SERVICE_COST_TYPE
} from "@zhihaoscm/meta";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { projectService } from "@/api/projectService";
import { contractService } from "@/api/contractService";
import { fileService } from "@/api/fileService";
import { purchaseGoodsReceiptService } from "@/api/purchaseGoodsReceiptService";
import { purchaseGoodsService } from "@/api/purchaseGoodsService";
import { purchaseInvoiceService } from "@/api/purchaseInvoiceService";
import { purchasePaymentService } from "@/api/purchasePaymentService";
import { purchaseReconciliationService } from "@/api/purchaseReconciliationService";
import { serviceCostBillService } from "@/api/serviceCostBillService";
import {
    Breadcrumb,
    DetailHeader,
    InfoCard,
    ProTable,
    ZhBaseItem,
    ZhGrid,
    ZhInsetTabs,
    type ProColumn
} from "@/components/common";
import { useAccountSession, useUserSession } from "@/stores/session";
import { formatNumber } from "@zhihaoscm/utils";
import dayjs from "dayjs";
import { PROJECT_TAB_ITEMS } from "../../../../../common/enum/project";
import { RECONCILIATION_QUERY_TYPE, RECONCILIATION_TYPES } from "../../../../../common/enum/reconciliation";
import "./console.css";
const projectTypes = PROJECT_TYPE.options.map(item => ({ label: item.label, value: Number(item.value) }));
const purchaseTypes = PURCHASE_TYPE.options.map(item => ({ label: item.label, value: Number(item.value) }));
const projectStates = PROJECT_STATE.options.map(item => ({ label: item.label, value: Number(item.value) }));
const enterpriseName = (enterprise?: any) =>
    enterprise?.name || enterprise?.legalRepresentative || enterprise?.realName || "--";
/** 项目列表按旧页面角色控制品类、采购类型、交易方和合同金额列。 */
export function ProjectListPage() {
    const navigate = useNavigate();
    const roleCode = useUserSession(state => state.roleCode) ?? ROLE_CODE.CUSTOMER.code;
    const customerId = useUserSession(state => state.userInfo.customer?.id);
    const isCustomer = roleCode === ROLE_CODE.CUSTOMER.code;
    const isSupplier = roleCode === ROLE_CODE.SUPPLIER.code;
    const isSupplyChain = roleCode === ROLE_CODE.SUPPLY_CHAIN.code;
    const title = `${isSupplyChain ? "联合采购" : isSupplier ? "客户" : "采购"}项目列表`;
    const columns = useMemo<ProColumn<ProjectVo>[]>(
        () => [
            {
                key: "code",
                title: "编号",
                minWidth: 170,
                search: { key: "keyword", type: "input", placeholder: "请输入项目编号或项目名称", span: 6, order: 4 },
                render: (_, row) => (
                    <Button
                        className="project-code-link"
                        type="link"
                        onClick={() => navigate(`/console/project/detail?id=${row.project.id}`)}
                    >
                        {row.project.code}
                    </Button>
                )
            },
            { key: "name", title: "名称", minWidth: 142, render: (_, row) => row.project.name || "--" },
            {
                key: "type",
                title: "项目类型",
                minWidth: 114,
                enum: projectTypes,
                search: { key: "type", type: "select", placeholder: "请选择项目类型", options: projectTypes, span: 3, order: 1 },
                render: (_, row) => PROJECT_TYPE.getIntroByCode(row.project.type)
            },
            ...(!isSupplier && !isCustomer
                ? [
                      {
                          key: "productTypeName",
                          title: "品类",
                          minWidth: 114,
                          render: (_: unknown, row: ProjectVo) => row.project.productTypeName || "--"
                      }
                  ]
                : []),
            ...(!isSupplyChain
                ? [
                      {
                          key: "purType",
                          title: "采购类型",
                          minWidth: 114,
                          search: {
                              key: "purType",
                              type: "select" as const,
                              placeholder: "请选择采购类型",
                              options: purchaseTypes,
                              span: 3,
                              order: 2
                          },
                          render: (_: unknown, row: ProjectVo) => PURCHASE_TYPE.getIntroByCode(row.project.purType)
                      },
                      {
                          key: "productName",
                          title: "采购商品",
                          minWidth: 114,
                          render: (_: unknown, row: ProjectVo) => row.project.productName || "--"
                      },
                      {
                          key: "contractPrice",
                          title: "合同金额(元)",
                          minWidth: 128,
                          render: (_: unknown, row: ProjectVo) => {
                              const extend = isCustomer
                                  ? row.projectExtends?.find(item => String(item.buyerId) === String(customerId))
                                  : undefined;
                              return formatNumber(
                                  isCustomer ? extend?.buyerContractAmount : row.project.scContractAmount,
                                  2
                              );
                          }
                      }
                  ]
                : []),
            ...(!isSupplyChain
                ? [
                      {
                          key: "supplyChain",
                          title: ROLE_CODE.SUPPLY_CHAIN.intro,
                          minWidth: 170,
                          render: (_: unknown, row: ProjectVo) => enterpriseName(row.project.supplyChainEnterprise)
                      }
                  ]
                : []),
            ...(!isCustomer
                ? [
                      {
                          key: "buyer",
                          title: "采购商",
                          minWidth: 170,
                          render: (_: unknown, row: ProjectVo) => {
                              const buyers = row.projectExtends ?? [];
                              const first = enterpriseName(buyers[0]?.buyerEnterprise);
                              return buyers.length > 1 ? (
                                  <Dropdown
                                      menu={{
                                          items: buyers.map((item, index) => ({
                                              key: String(item.id ?? index),
                                              label: enterpriseName(item.buyerEnterprise)
                                          }))
                                      }}
                                  >
                                      <button className="project-buyer-dropdown" type="button">
                                          {first} [<span>{buyers.length}</span>]
                                      </button>
                                  </Dropdown>
                              ) : (
                                  first
                              );
                          }
                      }
                  ]
                : []),
            ...(!isSupplier
                ? [
                      {
                          key: "supplier",
                          title: "供应商",
                          minWidth: 170,
                          render: (_: unknown, row: ProjectVo) => enterpriseName(row.project.supplierEnterprise)
                      }
                  ]
                : []),
            {
                key: "createdTime",
                title: "创建时间",
                width: 198,
                render: (_, row) =>
                    row.project.createdTime ? dayjs(row.project.createdTime).format("YYYY-MM-DD HH:mm:ss") : "--"
            },
            {
                key: "state",
                title: "状态",
                minWidth: 114,
                enum: projectStates,
                search: { key: "states", type: "select", placeholder: "请选择状态", options: projectStates, span: 3, order: 3 },
                render: (_, row) => PROJECT_STATE.getIntroByCode(row.project.state)
            }
        ],
        [customerId, isCustomer, isSupplier, isSupplyChain, navigate]
    );
    return (
        <section className="project-list-page section-card">
            <Breadcrumb routes={[{ name: "采购" }, { name: "项目" }]} />
            <div className="project-list-table">
                <ProTable
                    title={title}
                    rowKey={row => row.project.id}
                    columns={columns}
                    request={params => projectService.paging(params as never)}
                />
            </div>
        </section>
    );
}
const money = (value: unknown) => (value == null || value === "" ? "--" : `¥${formatNumber(Number(value), 2)}`);
const unitMoney = (value: unknown) => (value == null || value === "" ? "--" : `¥${formatNumber(Number(value), 2)}/吨`);
const percent = (value: unknown) => (value == null || value === "" ? "--" : `${value}%`);
const date = (value: unknown, format = "YYYY-MM-DD") => (value ? dayjs(value as string).format(format) : "--");
const normalizePage = <T extends object>(value: unknown) => {
    const data = value as { list?: T[]; total?: number } | T[] | undefined;
    return Array.isArray(data)
        ? { list: data, total: data.length }
        : { list: data?.list ?? [], total: data?.total ?? data?.list?.length ?? 0 };
};
/** 旧项目详情根据采购类型和交易方向计算各明细接口的 type。 */
function projectFlowType(detail: ProjectVo, isCustomer: boolean, flow: number) {
    if (PURCHASE_TYPE.DIRECT.match(detail.project.purType)) return PURCHASE_PAYMENT_TYPE.DOWNSTREAM_UPSTREAM.code;
    if (isCustomer || flow === PURCHASE_PAYMENT_TYPE.DOWNSTREAM_MIDSTREAM.code)
        return PURCHASE_PAYMENT_TYPE.DOWNSTREAM_MIDSTREAM.code;
    return PURCHASE_PAYMENT_TYPE.MIDSTREAM_UPSTREAM.code;
}
/** 对账明细使用独立的 1/2/3 类型，不能复用收付款流向枚举。 */
function projectReconciliationType(detail: ProjectVo, isCustomer: boolean, flow: number) {
    if (PURCHASE_TYPE.DIRECT.match(detail.project.purType)) return RECONCILIATION_TYPES.ONE.code;
    if (isCustomer || flow === PURCHASE_PAYMENT_TYPE.DOWNSTREAM_MIDSTREAM.code) return RECONCILIATION_TYPES.THREE.code;
    return RECONCILIATION_TYPES.TWO.code;
}
/** 项目信息面板逐项对应旧 projectBaseInfo.vue，不再展示后端对象键名。 */
function ProjectBasePanel({
    detail,
    buyerId,
    flow,
    isCustomer,
    isSupplier,
    isSupplyChain,
    accountId,
    type,
    onUpdate
}: {
    detail: ProjectVo;
    buyerId?: number;
    flow: number;
    isCustomer: boolean;
    isSupplier: boolean;
    isSupplyChain: boolean;
    accountId?: number;
    type: number;
    onUpdate: () => Promise<void>;
}) {
    const project = detail.project as any;
    const buyers = (detail.projectExtends ?? []) as any[];
    const direct = PURCHASE_TYPE.DIRECT.match(project.purType);
    const down = flow === PURCHASE_PAYMENT_TYPE.DOWNSTREAM_MIDSTREAM.code;
    const current =
        isSupplier && direct
            ? buyers[0]
            : buyers.find(item => String(item.buyerId) === String(isCustomer ? buyerId : buyerId));
    const useBuyer = isCustomer || (isSupplyChain && down && Boolean(buyerId)) || (isSupplier && direct);
    const source = useBuyer
        ? {
              unitPrice: current?.buyerUnitPrice,
              contractAmount: current?.buyerContractAmount,
              minPayLimit: current?.buyerMinPayLimit,
              payedAmount: current?.buyerPayedAmount,
              reconcileAmount: current?.buyerReconcileAmount,
              estimatedGoodsAmount: current?.buyerEstimatedGoodsAmount,
              frozenAmount: current?.buyerFrozenAmount,
              purQuantity: current?.purQuantity
          }
        : {
              unitPrice: project.scUnitPrice,
              contractAmount: project.scContractAmount,
              minPayLimit: project.scMinPayLimit,
              payedAmount: project.scPayedAmount,
              reconcileAmount: project.scReconcileAmount,
              estimatedGoodsAmount: project.scEstimatedGoodsAmount,
              frozenAmount: project.scFrozenAmount,
              purQuantity: project.purQuantity
          };
    const actualAmount = Math.max(0, Number(source.payedAmount ?? 0) - Number(source.reconcileAmount ?? 0));
    const canAmount = Math.max(
        0,
        Number(source.payedAmount ?? 0) - Number(source.frozenAmount ?? 0) - Number(source.estimatedGoodsAmount ?? 0)
    );
    const localEstimateTon = Number(source.unitPrice) > 0 ? Math.floor(canAmount / Number(source.unitPrice)) : 0;
    const [estimateTon, setEstimateTon] = useState(localEstimateTon);
    const [estimateAmount, setEstimateAmount] = useState(canAmount);
    const [contractFile, setContractFile] = useState<{ name: string; path: string }>();
    const [editing, setEditing] = useState(false);
    const [editingPrice, setEditingPrice] = useState("");
    const effectiveBuyerId = isSupplyChain
        ? down ? buyerId : project.supplyChainId
        : isCustomer ? accountId
        : direct ? buyers[0]?.buyerId : project.supplyChainId;
    const canChangePrice = project.state !== PROJECT_STATE.FINISHED.code && (isSupplier || (isSupplyChain && down));
    /** 可提货金额、吨数与合同文件均使用 Vue 项目信息组件的原接口。 */
    useEffect(() => {
        let active = true;
        const loadDerived = async () => {
            const buyerSide = isCustomer || (isSupplyChain && down) || (isSupplier && direct);
            const amount = buyerSide
                ? await projectService.findBuyerEstimatedAmount(project.id, effectiveBuyerId)
                : await projectService.findEstimatedAmount({ projectId: project.id, customerId: effectiveBuyerId });
            const ton = buyerSide
                ? await projectService.findBuyerEstimateTon(project.id, effectiveBuyerId)
                : await projectService.getQuantity({ projectId: project.id, customerId: effectiveBuyerId });
            const contracts = effectiveBuyerId
                ? await contractService.listByProjectIdAndType(project.id, type, effectiveBuyerId)
                : [];
            const file = contracts[0]?.fileId ? await fileService.findById(contracts[0].fileId) : undefined;
            if (active) { setEstimateAmount(amount); setEstimateTon(ton); setContractFile(file); }
        };
        void loadDerived().catch(() => {
            if (active) { setEstimateAmount(canAmount); setEstimateTon(localEstimateTon); setContractFile(undefined); }
        });
        return () => { active = false; };
    }, [accountId, buyerId, canAmount, direct, down, isCustomer, isSupplier, isSupplyChain, localEstimateTon, project.id, project.supplyChainId, type]);
    /** 预估单价校验、角色码和更新接口完全沿用旧 projectBaseInfo。 */
    const savePrice = async () => {
        if (!editingPrice) { void message.error("请输入预估单价"); return; }
        if (!/^(0\.0[1-9]|0\.[1-9][0-9]?|[1-9][0-9]{0,9}(\.[0-9]{1,2})?|0|10000000000)$/.test(editingPrice)) { void message.error("取值范围 0-100亿 保留两位小数"); return; }
        await projectService.updatePrice({
            id: project.id,
            customerId: effectiveBuyerId,
            frozenAmount: isSupplier && !direct ? project.scFrozenAmount : current?.buyerFrozenAmount,
            unitPrice: Number(editingPrice),
            roleCode: isSupplier && !direct ? ROLE_CODE.SUPPLY_CHAIN.code : ROLE_CODE.CUSTOMER.code
        } as any);
        void message.success("修改成功");
        setEditing(false);
        await onUpdate();
    };
    const aggregate = !buyerId && down && buyers.length > 0;
    return (
        <section className="section-card project-business-panel">
            <div className="section-title">交易信息</div>
            <ZhGrid cols={5} labelWidth={84}>
                {aggregate ? (
                    <>
                        <ZhBaseItem
                            label={`实际${isSupplier || buyers.length ? "收款" : "付款"}总金额`}
                            value={money(source.payedAmount)}
                        />
                        <ZhBaseItem label="实际对账总金额" value={money(source.reconcileAmount)} />
                        <ZhBaseItem label="可提货预估金额" value={money(estimateAmount)} />
                        <ZhBaseItem label="可提货预估吨数" value={formatNumber(estimateTon)} />
                        <ZhBaseItem label="实际余额" value={money(actualAmount)} />
                    </>
                ) : (
                    <>
                        <ZhBaseItem label="预估单价">
                            <span className="project-inline-price">{editing ? <><Input value={editingPrice} onChange={event => setEditingPrice(event.target.value)} /><Button type="link" onClick={() => void savePrice()}>保存</Button><Button type="link" onClick={() => setEditing(false)}>取消</Button></> : <>{money(source.unitPrice)}{canChangePrice ? <Button type="link" onClick={() => { setEditingPrice(String(source.unitPrice ?? "")); setEditing(true); }}>修改</Button> : null}</>}</span>
                        </ZhBaseItem>
                        <ZhBaseItem label="可提货预估吨数" value={formatNumber(estimateTon)} />
                        <ZhBaseItem label="可提货预估金额" value={money(estimateAmount)} />
                        <ZhBaseItem label="最低预付款提醒额度" value={money(source.minPayLimit)} />
                        <ZhBaseItem label="合同金额" value={money(source.contractAmount)} />
                        <ZhBaseItem
                            label={`实际${isSupplier || buyers.length ? "收款" : "付款"}总金额`}
                            value={money(source.payedAmount)}
                        />
                        <ZhBaseItem label="实际对账总金额" value={money(source.reconcileAmount)} />
                        <ZhBaseItem label="冻结金额" value={money(source.frozenAmount)} />
                        <ZhBaseItem label="采购吨数" value={formatNumber(source.purQuantity)} />
                        <ZhBaseItem label="实际余额" value={money(actualAmount)} />
                        <ZhBaseItem label="合同">{contractFile ? <span className="project-contract-file">{contractFile.name}<a href={contractFile.path} target="_blank" rel="noreferrer">查看/下载</a></span> : "--"}</ZhBaseItem>
                    </>
                )}
            </ZhGrid>
        </section>
    );
}
type DetailTableKind = "payment" | "delivery" | "receipt" | "reconciliation" | "service" | "invoice";
/** 六类业务明细复用旧 ProTable，但列、接口、标题和详情路径均保持旧组件定义。 */
function ProjectDetailTable({
    kind,
    detail,
    projectId,
    type,
    buyerId,
    flow,
    title,
    statistics
}: {
    kind: DetailTableKind;
    detail: ProjectVo;
    projectId: number;
    type: number;
    buyerId?: number;
    flow: number;
    title: string;
    statistics: any;
}) {
    const navigate = useNavigate();
    const roleCode = useUserSession(state => state.roleCode) ?? ROLE_CODE.CUSTOMER.code;
    const isCustomer = roleCode === ROLE_CODE.CUSTOMER.code;
    const isSupplier = roleCode === ROLE_CODE.SUPPLIER.code;
    const isSupplyChain = roleCode === ROLE_CODE.SUPPLY_CHAIN.code;
    const online = detail.project.type === PROJECT_TYPE.ONLINE.code;
    const [loadedCount, setLoadedCount] = useState(0);
    const [serviceStatics, setServiceStatics] = useState<{ count?: number; number?: number }>();
    const columns = useMemo<ProColumn<any>[]>(() => {
        if (kind === "payment")
            return [
                {
                    key: "id",
                    title: "编号",
                    minWidth: 170,
                    render: (_, row) => (
                        <Button
                            type="link"
                            onClick={() => navigate(`/console/payment/detail?id=${row.id}&payType=${title}`)}
                        >
                            {row.id}
                        </Button>
                    )
                },
                {
                    key: "amount",
                    title: "金额（元）",
                    minWidth: 142,
                    search: { key: "daterange", type: "dateRange", placeholder: "日期范围选择", span: 8 },
                    render: (_, row) => formatNumber(row.amount, 2)
                },
                { key: "payDate", title: "日期", minWidth: 142, render: (_, row) => date(row.payDate) }
            ];
        if (kind === "delivery")
            return [
                {
                    key: "id",
                    title: "编号",
                    minWidth: 170,
                    render: (_, row) => (
                        <Button
                            type="link"
                            onClick={() => navigate(`/console/purchaseGoods/detail?id=${row.id}&purGoodsType=${title}`)}
                        >
                            {row.id}
                        </Button>
                    )
                },
                {
                    key: "ships",
                    title: "船名",
                    minWidth: 142,
                    search: { key: "daterange", type: "dateRange", placeholder: "日期范围选择", span: 8 },
                    render: (_, row) =>
                        row.ships
                            ?.map((ship: any) => `${ship.cnName ? `${ship.cnName}/` : ""}${ship.name}`)
                            .join(" | ") || "--"
                },
                { key: "quantity", title: "吨数", minWidth: 142, render: (_, row) => formatNumber(row.quantity) },
                { key: "applyDate", title: "日期", minWidth: 142, render: (_, row) => date(row.applyDate) }
            ];
        if (kind === "receipt")
            return [
                {
                    key: "id",
                    title: "编号",
                    minWidth: 170,
                    render: (_, row) => (
                        <Button
                            type="link"
                            onClick={() => navigate(`/console/purchaseGoodsReceipt/detail?id=${row.id}`)}
                        >
                            {row.id}
                        </Button>
                    )
                },
                {
                    key: "quantity",
                    title: "吨数",
                    minWidth: 142,
                    search: { key: "daterange", type: "dateRange", placeholder: "日期范围选择", span: 8 },
                    render: (_, row) => formatNumber(row.quantity)
                },
                { key: "receiptConfirmedDate", title: "确认日期", minWidth: 142, render: (_, row) => date(row.receiptConfirmedDate) }
            ];
        if (kind === "reconciliation")
            return [
                {
                    key: "id",
                    title: "编号",
                    minWidth: 170,
                    render: (_, row) => (
                        <Button type="link" onClick={() => navigate(`/console/reconciliation/detail?id=${row.id}`)}>
                            {row.id}
                        </Button>
                    )
                },
                {
                    key: "actualAmount",
                    title: "折扣金额(元)",
                    minWidth: 142,
                    search: { key: "daterange", type: "dateRange", placeholder: "日期范围选择", span: 8 },
                    render: (_, row) => formatNumber(row.actualAmount, 2)
                },
                { key: "createdTime", title: "创建日期", minWidth: 142, render: (_, row) => date(row.createdTime) }
            ];
        if (kind === "service")
            return [
                {
                    key: "id",
                    title: "编号",
                    minWidth: 170,
                    search: { key: "daterange", type: "dateRange", placeholder: "日期范围选择", span: 8 },
                    render: (_, row) => (
                        <Button type="link" onClick={() => navigate(`/me/account/billDetail?id=${row.id}`)}>
                            {row.id}
                        </Button>
                    )
                },
                { key: "amount", title: "账单金额（元）", minWidth: 142, render: (_, row) => formatNumber(row.amount, 2) },
                {
                    key: "verificationAmount",
                    title: "实际结算金额（元）",
                    minWidth: 142,
                    render: (_, row) => formatNumber(row.verificationAmount, 2)
                },
                { key: "createdTime", title: "日期", minWidth: 142, render: (_, row) => date(row.createdTime) }
            ];
        return [
            {
                key: "id",
                title: "编号",
                minWidth: 170,
                render: (_, row) => (
                    <Button type="link" onClick={() => navigate(`/console/invoice/detail?id=${row.id}`)}>
                        {row.id}
                    </Button>
                )
            },
            {
                key: "amount",
                title: "申请金额（元）",
                minWidth: 142,
                search: { key: "daterange", type: "dateRange", placeholder: "日期范围选择", span: 8 },
                render: (_, row) => formatNumber(row.amount, 2)
            },
            { key: "createdTime", title: "创建日期", minWidth: 142, render: (_, row) => date(row.createdTime) }
        ];
    }, [kind, navigate, title]);
    const request = async (params: any) => {
        const query: any = { ...params, buyerId };
        if (query.daterange && kind !== "payment") {
            if (kind === "service") {
                const start = new Date(`${query.daterange[0]} 00:00:00`);
                const end = new Date(`${query.daterange[1]} 23:59:59`);
                query.startTime = new Date(+start + 8 * 3600 * 1000).toISOString();
                query.endTime = new Date(+end + 8 * 3600 * 1000).toISOString();
            } else {
                query.beginTime = dayjs(query.daterange[0]).format("YYYY-MM-DD");
                query.endTime = dayjs(query.daterange[1]).format("YYYY-MM-DD");
            }
            delete query.daterange;
        }
        let result;
        if (kind === "payment")
            result = normalizePage(await purchasePaymentService.getListByProjectId(projectId, type, query));
        if (kind === "delivery")
            result = normalizePage(await purchaseGoodsService.getListByProjectId(projectId, type, query));
        if (kind === "receipt")
            result = normalizePage(await purchaseGoodsReceiptService.getListByProjectId(projectId, type, query));
        if (kind === "reconciliation")
            result = normalizePage(await purchaseReconciliationService.getListByProjectId(projectId, type, query));
        if (kind === "invoice")
            result = normalizePage(await purchaseInvoiceService.getListByProjectId(projectId, type, query));
        if (kind === "service") {
            delete query.buyerId;
            result = normalizePage(await serviceCostBillService.getListByProjectId({ ...query, customerId: buyerId, projectId }));
        }
        const page = result ?? { list: [], total: 0 };
        setLoadedCount(page.list.length);
        return page;
    };
    useEffect(() => {
        if (kind !== "service") return;
        void serviceCostBillService.getStaticsByProjectId({ projectId }).then(setServiceStatics).catch(() => setServiceStatics(undefined));
    }, [kind, projectId]);
    const total =
        kind === "payment"
            ? statistics?.payment?.number
            : kind === "reconciliation"
              ? statistics?.reconciliation?.number
              : undefined;
    const header =
        kind === "payment" ? (
            <>
                <span>{title}明细</span>
                <span>
                    实际{title}总金额 {money(total)}
                </span>
            </>
        ) : kind === "reconciliation" ? (
            <>
                <span>{title}明细</span>
                <span>实际对账总金额 {money(total)}</span>
            </>
        ) : kind === "service" ? (
            <><span>服务费明细</span><span>已结算服务费总金额 {money(serviceStatics?.number)}</span></>
        ) : (
            <span>{kind === "delivery" ? `${isSupplyChain ? title : ""}提货申请` : title}</span>
        );
    const showAction = kind === "payment" ? title === "付款" && online
        : kind === "delivery" ? (isCustomer || (isSupplyChain && type === PURCHASE_PAYMENT_TYPE.MIDSTREAM_UPSTREAM.code)) && online
        : kind === "receipt" ? (isSupplier || (isSupplyChain && type === PURCHASE_PAYMENT_TYPE.DOWNSTREAM_MIDSTREAM.code)) && online
        : kind === "reconciliation" ? (isSupplier || (isSupplyChain && flow === PURCHASE_PAYMENT_TYPE.MIDSTREAM_UPSTREAM.code)) && online
        : kind === "invoice" ? (isCustomer || type === PURCHASE_PAYMENT_TYPE.DOWNSTREAM_MIDSTREAM.code) && online
        : false;
    const goCreate = () => {
        if (kind === "payment") navigate(`/console/payment/add?projectId=${projectId}`);
        if (kind === "delivery") navigate(`/console/purchaseGoods/add?projectId=${projectId}`);
        if (kind === "receipt") navigate(`/console/purchaseGoodsReceipt/add?projectId=${projectId}&projectName=${encodeURIComponent(detail.project.name)}`);
        if (kind === "invoice") navigate(`/console/invoice/form?projectId=${projectId}`);
        if (kind === "reconciliation") {
            const reconciliationType = isSupplyChain ? (flow === PURCHASE_PAYMENT_TYPE.DOWNSTREAM_MIDSTREAM.code ? RECONCILIATION_TYPES.THREE.code : RECONCILIATION_TYPES.TWO.code) : isCustomer ? RECONCILIATION_TYPES.THREE.code : RECONCILIATION_TYPES.TWO.code;
            const queryType = flow === PURCHASE_PAYMENT_TYPE.DOWNSTREAM_MIDSTREAM.code ? RECONCILIATION_QUERY_TYPE.ONE.code : RECONCILIATION_QUERY_TYPE.TWO.code;
            navigate(`/console/reconciliation/form?projectId=${projectId}&projectName=${encodeURIComponent(detail.project.name)}&type=${reconciliationType}&typeTabsActive=${queryType}`);
        }
    };
    const actionText = kind === "payment" ? `去${title}` : kind === "delivery" ? "去提货" : kind === "receipt" ? "去签收" : kind === "reconciliation" ? "去对账" : "去开票";
    const footerText = kind === "payment" ? `已${title}${loadedCount}笔` : kind === "delivery" ? `已提货${loadedCount}笔` : kind === "receipt" ? `已签收${loadedCount}笔` : kind === "reconciliation" ? `已对账${loadedCount}笔` : kind === "service" ? `已结算${serviceStatics?.count || 0}笔` : `已开票${loadedCount}笔`;
    return (
        <section className="section-card project-business-panel">
            <div className="section-title project-panel-title">{header}</div>
            <div className="project-detail-table">
                <ProTable<any> columns={columns} request={request} rowKey="id" searchPrefix={showAction ? () => <Button onClick={goCreate}>{actionText}</Button> : undefined} />
                <div className="project-table-total">{footerText}</div>
            </div>
        </section>
    );
}
/** 项目详情逐组件映射旧 detail/index.vue，保留角色、业务流与明细接口逻辑。 */
export function ProjectDetailPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const id = params.get("id") ?? "";
    const roleCode = useUserSession(state => state.roleCode) ?? ROLE_CODE.CUSTOMER.code;
    const accountId = useAccountSession(state => state.proxyActualAccount.id);
    const isCustomer = roleCode === ROLE_CODE.CUSTOMER.code;
    const isSupplier = roleCode === ROLE_CODE.SUPPLIER.code;
    const isSupplyChain = roleCode === ROLE_CODE.SUPPLY_CHAIN.code;
    const [detail, setDetail] = useState<ProjectVo>();
    const [statistics, setStatistics] = useState<any>({});
    const [flow, setFlow] = useState(PURCHASE_PAYMENT_TYPE.DOWNSTREAM_MIDSTREAM.code);
    const [buyerId, setBuyerId] = useState<number>();
    const [active, setActive] = useState(PROJECT_TAB_ITEMS.PROJECT_INFO.code);
    const load = useCallback(async () => {
        if (!id) return;
        try {
            const data = await projectService.getInfo(id);
            setDetail(data);
            if (isSupplyChain && data.projectExtends?.length === 1) setBuyerId(data.projectExtends[0].buyerId);
        } catch {
            void message.error("加载失败");
        }
    }, [id, isSupplyChain]);
    useEffect(() => {
        void load();
    }, [load]);
    const type = detail ? projectFlowType(detail, isCustomer, flow) : 1;
    const direct = detail ? PURCHASE_TYPE.DIRECT.match(detail.project.purType) : false;
    const statisticsBuyerId = isSupplyChain ? buyerId : isSupplier && !direct ? buyerId : accountId;
    useEffect(() => {
        if (!detail || !id) return;
        const statisticsType = isSupplyChain
            ? flow
            : isSupplier && !direct
              ? PURCHASE_PAYMENT_TYPE.MIDSTREAM_UPSTREAM.code
              : type;
        projectService
            .statistics(Number(id), statisticsType, statisticsBuyerId)
            .then(setStatistics)
            .catch(() => setStatistics({}));
    }, [active, buyerId, detail, direct, flow, id, isSupplier, isSupplyChain, statisticsBuyerId, type]);
    if (!detail) return null;
    const project = detail.project as any;
    const product = detail.product as any;
    const buyers = (detail.projectExtends ?? []) as any[];
    const buyerOptions = buyers.map(item => ({ label: enterpriseName(item.buyerEnterprise), value: item.buyerId }));
    const serviceCost = isSupplyChain
        ? project.serviceCosts?.[1]
        : isSupplier
          ? project.serviceCosts?.[0]
          : buyers.find(item => String(item.buyerId) === String(accountId))?.serviceCosts;
    const tabs = PROJECT_TAB_ITEMS.options
        .filter(
            item => project.type !== PROJECT_TYPE.OFFLINE.code || item.value !== PROJECT_TAB_ITEMS.BILLING_INFO.code
        )
        .map(item => ({
            key: String(item.value),
            label:
                item.value === PROJECT_TAB_ITEMS.PAYMENT_INFO.code
                    ? `${isCustomer ? "付款" : isSupplier ? "收款" : flow === PURCHASE_PAYMENT_TYPE.DOWNSTREAM_MIDSTREAM.code ? "收款" : "付款"}明细`
                    : item.label
        }));
    const tableBuyerId = isCustomer
        ? accountId
        : isSupplier
          ? direct
              ? buyers[0]?.buyerId
              : project.supplyChainId
          : flow === PURCHASE_PAYMENT_TYPE.DOWNSTREAM_MIDSTREAM.code
            ? buyerId
            : project.supplyChainId;
    let panel: ReactNode = (
        <ProjectBasePanel
            detail={detail}
            buyerId={isCustomer ? accountId : buyerId}
            flow={flow}
            isCustomer={isCustomer}
            isSupplier={isSupplier}
            isSupplyChain={isSupplyChain}
            accountId={accountId}
            type={type}
            onUpdate={load}
        />
    );
    if (active === PROJECT_TAB_ITEMS.PAYMENT_INFO.code)
        panel = (
            <ProjectDetailTable
                kind="payment"
                detail={detail}
                projectId={Number(id)}
                type={type}
                buyerId={tableBuyerId}
                flow={flow}
                title={
                    isCustomer || (isSupplyChain && flow !== PURCHASE_PAYMENT_TYPE.DOWNSTREAM_MIDSTREAM.code)
                        ? "付款"
                        : "收款"
                }
                statistics={statistics}
            />
        );
    if (active === PROJECT_TAB_ITEMS.DELIVERY_INFO.code)
        panel = (
            <ProjectDetailTable
                kind="delivery"
                detail={detail}
                projectId={Number(id)}
                type={type}
                buyerId={tableBuyerId}
                flow={flow}
                title={isSupplyChain ? (flow === PURCHASE_PAYMENT_TYPE.DOWNSTREAM_MIDSTREAM.code ? "下游" : "我的") : ""}
                statistics={statistics}
            />
        );
    if (active === PROJECT_TAB_ITEMS.SIGN_INFO.code)
        panel = (
            <ProjectDetailTable
                kind="receipt"
                detail={detail}
                projectId={Number(id)}
                type={type}
                buyerId={tableBuyerId}
                flow={flow}
                title={`${isSupplyChain ? (flow === PURCHASE_PAYMENT_TYPE.DOWNSTREAM_MIDSTREAM.code ? "我的" : "供应商") : ""}签收明细`}
                statistics={statistics}
            />
        );
    if (active === PROJECT_TAB_ITEMS.RECONCILIATION_INFO.code)
        panel = (
            <ProjectDetailTable
                kind="reconciliation"
                detail={detail}
                projectId={Number(id)}
                type={projectReconciliationType(detail, isCustomer, flow)}
                buyerId={tableBuyerId}
                flow={flow}
                title={
                    isSupplyChain
                        ? flow === PURCHASE_PAYMENT_TYPE.DOWNSTREAM_MIDSTREAM.code
                            ? "对账下游"
                            : "供应商对账确认"
                        : "对账"
                }
                statistics={statistics}
            />
        );
    if (active === PROJECT_TAB_ITEMS.SERVICE_COST_INFO.code)
        panel = (
            <ProjectDetailTable
                kind="service"
                detail={detail}
                projectId={Number(id)}
                type={type}
                buyerId={accountId}
                flow={flow}
                title="服务费明细"
                statistics={statistics}
            />
        );
    if (active === PROJECT_TAB_ITEMS.BILLING_INFO.code)
        panel = (
            <ProjectDetailTable
                kind="invoice"
                detail={detail}
                projectId={Number(id)}
                type={type}
                buyerId={tableBuyerId}
                flow={flow}
                title={`${isSupplyChain ? (flow === PURCHASE_PAYMENT_TYPE.DOWNSTREAM_MIDSTREAM.code ? "下游开票" : "开票申请") : "开票"}明细`}
                statistics={statistics}
            />
        );
    return (
        <section className="project-detail-page detail-container">
            <DetailHeader
                title={`${isSupplyChain ? "联合采购" : isSupplier ? "客户" : "采购"}项目详情`}
                leftInfos={[{ label: "编号", value: project.code }]}
                rightInfos={[{ label: "创建时间", value: date(project.createdTime, "YYYY-MM-DD HH:mm:ss") }]}
                state={{ text: PROJECT_STATE.getIntroByCode(project.state) ?? undefined, color: project.state === PROJECT_STATE.FINISHED.code ? "#999999" : "#3370ff" }}
                extraInfo={<span className="project-detail-type">{PROJECT_TYPE.getIntroByCode(project.type)}</span>}
                menu={
                    <Breadcrumb
                        routes={[
                            { name: "采购" },
                            { name: "项目", path: "/console/project" },
                            { name: "项目详情" }
                        ]}
                    />
                }
            />
            <InfoCard title="商品信息">
                <ZhGrid cols={5} labelWidth={70}>
                    <ZhBaseItem label="商品">
                        <button
                            className="project-detail-link"
                            onClick={() => product?.id && window.open(`/public/trade/product/${product.id}`, "_blank")}
                            type="button"
                        >
                            {product?.name || project.productName || "--"}
                        </button>
                    </ZhBaseItem>
                    <ZhBaseItem label="商品单价" value={money(product?.unitPrice)} />
                    <ZhBaseItem label="品类" value={project.productTypeName || "--"} />
                    <ZhBaseItem label="规格" value={product?.productTypeSpec || "--"} />
                    <ZhBaseItem label="含泥量" value={percent(product?.siltContent)} />
                    <ZhBaseItem label="石粉MB值" value={percent(product?.stonePowderValue)} />
                    <ZhBaseItem label="细粉含量" value={percent(product?.finePowderContent)} />
                    <ZhBaseItem label="压碎值" value={percent(product?.crushValue)} />
                    <ZhBaseItem label="含水率" value={percent(product?.moistureContent)} />
                </ZhGrid>
            </InfoCard>
            <InfoCard title="采购信息">
                <ZhGrid cols={5} labelWidth={70}>
                    <ZhBaseItem label="项目名称" value={project.name} />
                    {!isSupplier ? (
                        <ZhBaseItem label="供应商" value={enterpriseName(project.supplierEnterprise)} />
                    ) : null}
                    <ZhBaseItem
                        label={ROLE_CODE.SUPPLY_CHAIN.intro}
                        value={enterpriseName(project.supplyChainEnterprise)}
                    />
                    {isSupplyChain ? (
                        <ZhBaseItem
                            label="采购商"
                            value={buyers.map(item => enterpriseName(item.buyerEnterprise)).join("、") || "--"}
                        />
                    ) : null}
                    <ZhBaseItem label="介绍" span={5} value={project.remark || "--"} />
                </ZhGrid>
            </InfoCard>
            {PROJECT_TYPE.ONLINE.match(project.type) ? (
                <InfoCard title="服务费信息">
                    <ZhGrid cols={5} labelWidth={70}>
                        <ZhBaseItem
                            label="结算方式"
                            value={SERVICE_COST_TYPE.getIntroByCode(serviceCost?.type) || "--"}
                        />
                        <ZhBaseItem
                            label={SERVICE_COST_TYPE.UNIT_PRICE.match(serviceCost?.type) ? "单价" : "总金额"}
                            value={
                                SERVICE_COST_TYPE.UNIT_PRICE.match(serviceCost?.type)
                                    ? unitMoney(serviceCost?.amount)
                                    : money(serviceCost?.amount)
                            }
                        />
                    </ZhGrid>
                </InfoCard>
            ) : null}
            <div className="common-card project-detail-business">
                {isSupplyChain ? (
                    <div className="project-flow-row">
                        <ZhInsetTabs
                            active={flow}
                            onChange={value => {
                                setFlow(value);
                                setBuyerId(undefined);
                            }}
                            options={[
                                { label: "与采购商", value: PURCHASE_PAYMENT_TYPE.DOWNSTREAM_MIDSTREAM.code },
                                { label: "与供应商", value: PURCHASE_PAYMENT_TYPE.MIDSTREAM_UPSTREAM.code }
                            ]}
                        />
                        {flow === PURCHASE_PAYMENT_TYPE.DOWNSTREAM_MIDSTREAM.code ? (
                            buyerOptions.length === 1 ? (
                                <div className="project-one-buyer">{buyerOptions[0].label}</div>
                            ) : (
                                <Select
                                    allowClear
                                    className="project-buyer-select"
                                    onChange={setBuyerId}
                                    options={buyerOptions}
                                    placeholder="全部"
                                    value={buyerId}
                                />
                            )
                        ) : null}
                    </div>
                ) : null}
                <Tabs activeKey={String(active)} items={tabs} onChange={key => setActive(Number(key))} />
                {panel}
            </div>
            <div className="project-detail-actions">
                <Button type="primary" onClick={() => navigate("/console/project")}>
                    返回
                </Button>
            </div>
        </section>
    );
}
